import React, { useEffect } from 'react';
import { Container, Box, Typography, Card, CardContent, Avatar, Divider } from '@mui/material';
import { 
  WarningAmber, 
  Info, 
  Gavel, 
  LocationOn, 
  PrivacyTip, 
  Description, 
  ContactSupport,
  Lightbulb,
  AccountBalance
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const keyPoints = [
    {
      icon: <Info />,
      text: t('about_info_only')
    },
    {
      icon: <Gavel />,
      text: t('about_not_affiliated')
    },
    {
      icon: <LocationOn />,
      text: t('about_state_rules_vary')
    }
  ];

  const linkCards = [
    {
      icon: <PrivacyTip />,
      title: t('about_privacy_title'),
      subtitle: t('about_privacy_subtitle'),
      path: '/privacy_policy'
    },
    {
      icon: <Description />,
      title: t('about_terms_title'),
      subtitle: t('about_terms_subtitle'),
      path: '/terms_of_service'
    },
    {
      icon: <ContactSupport />,
      title: t('about_contact_title'),
      subtitle: t('about_contact_subtitle'),
      path: '/contact'
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Website Info Card */}
      <Card elevation={0} sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <Avatar
            src="/icon.jpg"
            sx={{ 
              width: 96, 
              height: 96, 
              margin: '0 auto',
              mb: 3,
              border: '3px solid #ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <AccountBalance sx={{ fontSize: 48, color: '#673AB7' }} />
          </Avatar>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
            {t('nagrik_sahayak')}
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 400 }}>
            {t('nagrik_sahayak_hindi')}
          </Typography>
        </CardContent>
      </Card>

      {/* Disclaimer Section */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 4,
          bgcolor: '#FFF8E1',
          border: '2px solid #FFB74D'
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
            <WarningAmber sx={{ color: '#F57C00', fontSize: 36, mr: 2 }} />
            <Typography variant="h4" component="h2" color="#E65100" sx={{ fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' } }}>
              {t('about_disclaimer')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#424242' }}>
            {t('about_disclaimer_p1')}
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#424242' }}>
            {t('about_disclaimer_p2')}
          </Typography>

          {/* Key Points */}
          <Box sx={{ mt: 4 }}>
            {keyPoints.map((point, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                <Box sx={{ color: '#F57C00', mr: 2, mt: 0.5 }}>
                  {point.icon}
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#424242' }}>
                  {point.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Legal & Support Links */}
      {linkCards.map((card, index) => (
        <Card 
          key={index}
          elevation={0} 
          sx={{ 
            mb: 2.5,
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(103, 58, 183, 0.12)',
              transform: 'translateY(-2px)',
              borderColor: 'primary.light'
            }
          }}
          onClick={() => navigate(card.path)}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(103,58,183,0.08)',
                  color: '#673AB7',
                  width: 56,
                  height: 56,
                  mr: 2.5
                }}
              >
                {card.icon}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h3">
                  {card.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </Box>
              <Box sx={{ color: '#BDBDBD' }}>→</Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Footer Note */}
      <Card 
        elevation={0} 
        sx={{ 
          mt: 4,
          bgcolor: '#E3F2FD',
          border: '2px solid #90CAF9'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Lightbulb sx={{ color: '#1976D2', mr: 2, fontSize: 28 }} />
            <Typography variant="body1" color="#0D47A1" sx={{ fontWeight: 500, lineHeight: 1.7 }}>
              {t('about_verify_note')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};
